"use client";

import React from "react";
import { Flex } from "antd";
import Title from "antd/es/typography/Title";
import Text from "antd/es/typography/Text";
import Paragraph from "antd/es/typography/Paragraph";
import ManageAppointmentButtons from "./components/ManageAppointmentButtons";
import { formatPrice, formatDuration } from "@openings/booking-core";
import { buildScopedBookingHref } from "./bookingHref";
import type { SalonDTO } from "@openings/api-client";
import type { AppointmentManageDTO } from "./types";

interface AppointmentManageProps {
  salon: SalonDTO;
  data: AppointmentManageDTO;
  bookHref?: string;
  bookCtaLabel?: string;
  emptyStateMessage?: string;
  /** Dev harness only: render these appointments in their post-cancel state. */
  devCanceledAppointmentIds?: readonly string[];
}

type Appointment = AppointmentManageDTO["upcomingAppointments"][number];

interface AppointmentItemProps {
  appointment: Appointment;
  salon: SalonDTO;
  bookingHref: string;
  bookingLabel: string;
  devInitialCanceled?: boolean;
}

const AppointmentItem = ({
  appointment,
  salon,
  bookingHref,
  bookingLabel,
  devInitialCanceled,
}: AppointmentItemProps) => (
  <Flex
    vertical
    gap={6}
    style={{
      width: "100%",
      padding: 16,
      boxSizing: "border-box",
      borderRadius: 10,
      // Skin vars exist only under a skin; fallbacks are production.
      border: "1px solid var(--openings-booking-skin-control-border, #e5e5e5)",
      background: "var(--openings-booking-skin-surface, #fafafa)",
    }}
  >
    <Text strong style={{ fontSize: 15 }}>
      {appointment.serviceName}
    </Text>
    <Flex wrap="wrap" gap="6px 12px">
      <Text type="secondary" style={{ fontSize: 13 }}>
        {appointment.datetime}
      </Text>
      {appointment.userName && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          with {appointment.userName}
        </Text>
      )}
    </Flex>
    <Flex wrap="wrap" gap="2px 8px">
      {appointment.servicePrice > 0 && (
        <Text
          type="secondary"
          style={{ fontSize: 13, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {formatPrice(appointment.servicePrice)}
        </Text>
      )}
      {appointment.serviceDuration > 0 && (
        <Text
          type="secondary"
          style={{ fontSize: 13, whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {formatDuration(appointment.serviceDuration)}
        </Text>
      )}
      {appointment.address && (
        <Text type="secondary" style={{ fontSize: 13, minWidth: 0 }}>
          · {appointment.address}
        </Text>
      )}
    </Flex>
    {(appointment.parking?.description || appointment.parking?.link) && (
      <Flex vertical gap={2}>
        {appointment.parking?.description && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            Parking: {appointment.parking.description}
          </Text>
        )}
        {appointment.parking?.link && (
          <a
            href={appointment.parking.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, textDecoration: "underline" }}
          >
            Open parking map
          </a>
        )}
      </Flex>
    )}
    <div style={{ marginTop: 8 }}>
      <ManageAppointmentButtons
        appointmentId={appointment.id}
        salonName={salon.name}
        salonPhoneNumber={appointment.salonPhoneNumber}
        memberPhoneNumber={appointment.memberPhoneNumber}
        memberName={appointment.userName}
        startsAt={appointment.startsAt}
        cancellationPolicyEnabled={salon.cancellationPolicyEnabled !== false}
        cancellationPolicy={{
          hours: salon.cancellationPolicyHours,
          note: salon.cancellationPolicyNote,
          lateGraceMinutes: salon.lateGraceMinutes,
        }}
        bookingHref={buildScopedBookingHref(
          bookingHref,
          appointment.bookingScheduleId,
        )}
        bookingLabel={bookingLabel}
        devInitialCanceled={devInitialCanceled}
      />
    </div>
  </Flex>
);

const AppointmentManage = ({
  data,
  salon,
  bookHref = "/",
  bookCtaLabel = "Book an Appointment",
  emptyStateMessage = "You currently have no upcoming appointments.",
  devCanceledAppointmentIds,
}: AppointmentManageProps) => {
  return (
    <main aria-label="Manage Appointments">
      <Flex vertical gap={12}>
        <div>
          <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
            Manage Appointments
          </Title>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            View, reschedule, or cancel upcoming visits.
          </Paragraph>
        </div>

        {data.upcomingAppointments.length === 0 ? (
          <Text type="secondary" style={{ padding: "16px 0" }}>
            {emptyStateMessage}
          </Text>
        ) : (
          <Flex vertical gap={10}>
            {data.upcomingAppointments.map((appointment) => (
              <AppointmentItem
                key={appointment.id}
                appointment={appointment}
                salon={salon}
                bookingHref={bookHref}
                bookingLabel={bookCtaLabel}
                devInitialCanceled={devCanceledAppointmentIds?.includes(
                  appointment.id,
                )}
              />
            ))}
          </Flex>
        )}
      </Flex>
    </main>
  );
};

export default AppointmentManage;
